import React from 'react';
import SanitizedHtml from 'src/components/ui/SafeHtml/SanitizedHtml';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Case, useCaseMutations, useCaseSectionsQuery } from 'src/services/case';
import { getDetailViewUrl } from 'src/Layout';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Divider from 'src/components/ui/Divider/Divider';
import { Attachment } from 'src/components/layouts/Chat/Attachment';
import { CasePriorityBadge, CaseStatusBadge, getFormattedDay, getModelName } from './CasesOverview';

type IconTableRowProps = {
  icon: IconName;
  label: React.ReactNode;
  content: React.ReactNode;
};

const IconTableRow = ({ icon, label, content }: IconTableRowProps) => {
  return (
    <>
      <Icon name={icon} className="text-faded" />
      <div className="typo-text-14--bold text-faded pt-2">{label}</div>
      <div className="typo-text-14 text-full pt-2">{content}</div>
    </>
  );
};

const IconTable = ({ rows }: { rows: IconTableRowProps[] }) => {
  return (
    <div className="grid grid-cols-[min-content_minmax(min-content,156px)_1fr] gap-16">
      {rows
        .filter(row => row.content)
        .map(row => (
          <IconTableRow key={`${row.icon}-${row.label}-${row.label}`} {...row} />
        ))}
    </div>
  );
};

export const CaseDescription = ({ descriptionHtml }: { descriptionHtml: string }) => {
  return (
    <article className="surface-elevated flex justify-center py-24 px-16 border-distinct rounded-base">
      <SanitizedHtml className="max-w-[52rem] typo-text-14 italic whitespace-pre-line" html={descriptionHtml} />
    </article>
  );
};

export const Header = ({ fetchedCase, hideAction }: { fetchedCase: Case; hideAction?: boolean }) => {
  const { t } = useTranslation();
  const { setDoneMutation } = useCaseMutations();

  return (
    <div className="grid grid-cols-[1fr_min-content] gap-32">
      <div className="flex flex-col gap-y-12">
        <h2 className="text-full typo-title-36">{fetchedCase.topic}</h2>
        <span className="text-faded typo-text-14">
          {t('case.created_on', { date: getFormattedDay(new Date(fetchedCase.timestamp)) })}
        </span>
      </div>

      {fetchedCase?.state?.name.toLowerCase() !== 'done' && !hideAction && (
        <Button
          title={t('case.confirm_and_solve')}
          tone={ButtonToneProps.PRIMARY_BLUE}
          className="mt-4"
          onClick={async () => {
            if (fetchedCase.id) {
              await setDoneMutation.mutateAsync({
                params: {
                  path: {
                    model: 'project.task',
                  },
                },
                body: [fetchedCase.id],
              });
            }
          }}
        />
      )}
    </div>
  );
};

export const OverlayContent = ({ fetchedCase, allowEdit }: { fetchedCase: Case; allowEdit?: boolean }) => {
  const { t } = useTranslation();

  const sections = useCaseSectionsQuery()?.data as { key: string; label: string }[] | undefined;
  const modelNames = sections ? Object.fromEntries(sections.map(model => [model.key, model.label])) : null;
  const modelName = fetchedCase.section ? getModelName(fetchedCase.section, modelNames) : undefined;

  const resourceLink =
    fetchedCase.section && fetchedCase.resource_id
      ? getDetailViewUrl(fetchedCase.section, fetchedCase.resource_id)
      : null;

  return (
    <div className="flex flex-col gap-y-32">
      <Header fetchedCase={fetchedCase} hideAction={!allowEdit} />
      <Divider />
      <IconTable
        rows={[
          { icon: 'folderOpen', label: t('case.topic'), content: fetchedCase.topic },
          {
            icon: 'arrowUpward',
            label: t('case.priority'),
            content: fetchedCase.priority !== '0' && <CasePriorityBadge status={fetchedCase.priority} />,
          },
          { icon: 'account-tree', label: t('case.section'), content: modelName || '' },
          {
            icon: 'link',
            label: t('case.resource_id'),
            content: resourceLink ? (
              <Link className="underline" to={resourceLink}>
                {fetchedCase.resource_id}
              </Link>
            ) : (
              fetchedCase.resource_id
            ),
          },
          {
            icon: 'accountCircle',
            label: t('case.assigned_to'),
            content: fetchedCase.assigned_user?.map(u => u.name)?.join(', '),
          },
          {
            icon: 'calendar-today',
            label: t('case.due_date'),
            content: fetchedCase.due_date && getFormattedDay(new Date(fetchedCase.due_date)),
          },
          {
            icon: 'labelImportant',
            label: t('case.state'),
            content: fetchedCase.state && <CaseStatusBadge status={fetchedCase.state} />,
          },
        ]}
      />
      <div className="flex flex-col gap-y-16">
        {(fetchedCase.description || fetchedCase.attachments) && <Divider className="mb-16" />}
        <div className="flex flex-col gap-8">
          <span className="typo-text-14--bold text-faded">{t('general.description')}</span>
          <CaseDescription descriptionHtml={fetchedCase?.description ? fetchedCase.description : ''} />
        </div>
        {fetchedCase.attachments && fetchedCase.attachments.length > 0 && (
          <div className="flex flex-col gap-8 mt-8">
            <p className="typo-text-14--bold text-faded">{t('case.attachments')}</p>
            <div className="flex flex-wrap gap-16">
              {fetchedCase.attachments.map(attachment => (
                <div key={attachment.id} className="self-start">
                  <Attachment
                    attachment={{
                      ...attachment,
                      filename: attachment.name || '',
                      id: attachment.id || 0,
                      mimetype: attachment.mimetype || '',
                    }}
                    t={t}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {(fetchedCase.description || fetchedCase.attachments) && <Divider className="mt-16" />}
      </div>
    </div>
  );
};
